import { EFFECTS, type AuthoringStep } from '@capture/shared';

export interface IdentifiedStep {
  effectKey: string;
  label: string;
  step: AuthoringStep;
}

const FILL_TRANSFORM = EFFECTS.filter((e) => e.bucket === 'fill_transform' && typeof e.parse === 'function');

/**
 * Identify a persisted Behavior as one of the Fill/transform effects by asking
 * each effect's registry `parse` to claim it. Returns the first match (kinds are
 * unique per effect) or null (e.g. display/emit — homed elsewhere, not here).
 */
export function identifyFillTransformStep(behavior: unknown): IdentifiedStep | null {
  for (const e of FILL_TRANSFORM) {
    const step = e.parse!(behavior);
    if (step) return { effectKey: e.key, label: e.label, step };
  }
  return null;
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}
function nestedSource(v: unknown): string | undefined {
  return v && typeof v === 'object' ? str((v as { source?: unknown }).source) : undefined;
}

/** One-line, defensive human summary for the read-only list. */
export function summarizeEffectStep(effectKey: string, config: Record<string, unknown>): string {
  switch (effectKey) {
    case 'compute':
      return nestedSource(config.formula) ?? '(expression)';
    case 'substitute':
      return nestedSource(config.value) ?? '(mapped value)';
    case 'transform':
      return str(config.op) ?? '(reformat)';
    case 'parse_split':
      return str(config.parser) ?? '(split)';
    case 'populate_source': {
      const src = config.source;
      const type = src && typeof src === 'object' ? str((src as { type?: unknown }).type) : undefined;
      return type ?? '(source)';
    }
    case 'webhook_lookup':
      return str(config.url) ?? '(external call)';
    default:
      return '';
  }
}
